import {
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Radio,
  Row,
  Skeleton,
  Space,
  Switch,
  theme,
  Typography,
} from 'antd'
import { Trans, useTranslation } from 'react-i18next'
import { useProfilesSelector } from '../../lib/profiles'
import { CardHeader } from '../CardHeader'
import {
  useFirestore,
  useFirestoreCollectionData,
  useFirestoreDocData,
  useUser,
} from 'reactfire'
import { collection, doc, query, setDoc, where } from 'firebase/firestore'
import { gutter } from '../../lib/layout'
import { useMemo, useState } from 'react'
import { errorResponse } from '../../lib/errorResponse'
import NoNotifications from '../../resources/img/no-message.svg'
import { useIsAutomationAdmin } from '../../lib/useIsAutomationAdmin'
import { useIsChatAgent } from '../../lib/useIsChatAgent'
import { useIsAdmin } from '../../lib/useIsAdmin'
import { asChatwootSoundMode } from '../../lib/useChatwootSoundPreference'
import { useCompanyAudienceFlag } from '../../lib/useChatwootEnabled'
import { FEATURE_FLAGS } from '../../lib/featureFlagKeys'

// A type that is only ever sent by email: there is no in-app channel to show.
function EmailOnlySetting({ name, value, label, description }) {
  const { t } = useTranslation()

  return (
    <>
      <Col xs={24} md={6} style={{ fontWeight: '500' }}>
        {label}
      </Col>
      <Col xs={24} md={18}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Form.Item
            style={{ margin: 0 }}
            name={name}
            initialValue={value ?? ['email']}
          >
            <Checkbox.Group style={{ width: '100%' }}>
              <Row>
                <Col xs={24} md={12} xl={8}>
                  <Checkbox value="email">
                    {t('frontend:myAccount.notifications.channelEmail')}
                  </Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 'small' }}>
            {description}
          </Typography.Text>
        </Space>
      </Col>
    </>
  )
}

export function NotificationSettings() {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const profiles = useProfilesSelector()

  const isEmployee = profiles?.some((profile) =>
    profile.notificationsContext.startsWith('employee')
  )

  const isEmployer = profiles?.some((profile) =>
    profile.notificationsContext.startsWith('employer')
  )

  const isManagedByTaxAdvisor = profiles?.some(
    (profile) => profile.managedByTaxAdvisor
  )

  const isTaxAdvisor = profiles?.some((profile) =>
    profile.notificationsContext.startsWith('tax-advisor')
  )

  // Both claims resolve asynchronously via getIdTokenResult, and both report
  // false while they do. Without the loading flags an operator whose only role
  // is one of these is told they have no notification settings, and then the
  // section pops in a moment later.
  const { isAutomationAdmin, loading: automationAdminLoading } =
    useIsAutomationAdmin()
  const { isChatAgent, loading: chatAgentLoading } = useIsChatAgent()
  const { isAdmin, loading: adminLoading } = useIsAdmin()
  // Gate this exactly where the inbox itself is gated (operations/communication
  // admits `isAdmin || isChatAgent`). Gating the setting on the rollout claim
  // alone leaves every admin-based operator hearing a chime with no control
  // for it.
  const canUseSupportInbox = isAdmin || isChatAgent

  const { token } = theme.useToken()

  const firestore = useFirestore()
  const user = useUser()

  const { data: userProfile } = useFirestoreDocData(
    doc(firestore, 'userprofiles', user.data.uid)
  )
  const notificationSettings = userProfile?.notificationSettings

  const employerCompanyUids = useMemo(
    () =>
      (profiles ?? [])
        .filter((profile) =>
          profile.notificationsContext.startsWith('employer/')
        )
        .map((profile) => profile.notificationsContext.split('/')[1]),
    [profiles]
  )
  const { data: ownContracts } = useFirestoreCollectionData(
    query(
      collection(firestore, 'contracts'),
      where('employee.userUid', '==', user.data.uid),
      where('deleted', '==', false)
    ),
    { idField: 'uid' }
  )
  const employeeCompanyUids = useMemo(
    () =>
      (ownContracts ?? [])
        .map((contract) => contract.employer?.uid)
        .filter(Boolean),
    [ownContracts]
  )
  const { enabled: leaveRequestsAsEmployer } = useCompanyAudienceFlag(
    FEATURE_FLAGS.LEAVE_REQUESTS_ENABLED,
    employerCompanyUids,
    false
  )
  const { enabled: leaveRequestsAsEmployee } = useCompanyAudienceFlag(
    FEATURE_FLAGS.LEAVE_REQUESTS_ENABLED,
    employeeCompanyUids,
    false
  )

  const [saving, setSaving] = useState(false)

  async function submit(values) {
    setSaving(true)
    try {
      await setDoc(
        doc(firestore, `userprofiles/${user.data.uid}`),
        {
          notificationSettings: values,
        },
        { merge: true }
      )
    } catch (e) {
      errorResponse(e)
    } finally {
      setSaving(false)
    }
  }

  const employerInvoiceEmail = Form.useWatch(['employer', 'new_invoice'], {
    form,
    preserve: true,
  })

  const taxAdvisorInvoiceEmail = Form.useWatch(['tax-advisor', 'new_invoice'], {
    form,
    preserve: true,
  })

  if (
    !isEmployer &&
    !isEmployee &&
    !isTaxAdvisor &&
    !isAutomationAdmin &&
    !canUseSupportInbox &&
    !automationAdminLoading &&
    !chatAgentLoading &&
    !adminLoading
  ) {
    return (
      <>
        <CardHeader>{t('frontend:myAccount.notifications.heading')}</CardHeader>
        <Card bordered={false}>
          <Empty
            image={NoNotifications}
            description={t('frontend:myAccount.notifications.noneAvailable')}
          />
        </Card>
      </>
    )
  }

  if (userProfile === undefined) {
    return (
      <>
        <CardHeader>{t('frontend:myAccount.notifications.heading')}</CardHeader>
        <Card bordered={false}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      </>
    )
  }

  return (
    <Form colon={false} layout="horizontal" form={form} onFinish={submit}>
      <CardHeader style={{ marginBottom: 0, lineHeight: '1em' }}>
        {t('frontend:myAccount.notifications.heading')}
      </CardHeader>
      {isEmployee && (
        <>
          <CardHeader style={{ marginTop: token.marginLG }} level={5}>
            {t('frontend:myAccount.notifications.employeeSection')}
          </CardHeader>
          <Card bordered={false}>
            <Row gutter={gutter}>
              <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                {t('frontend:myAccount.notifications.newDocumentsLabel')}
              </Col>
              <Col xs={24} md={18}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item
                    style={{ margin: 0 }}
                    name={['employee', 'new_documents']}
                    initialValue={
                      notificationSettings?.employee?.new_documents ?? [
                        'app',
                        'email',
                      ]
                    }
                  >
                    <Checkbox.Group
                      style={{ width: '100%' }}
                      value={['email', 'app']}
                    >
                      <Row>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="app" disabled checked>
                            {t(
                              'frontend:myAccount.notifications.channelAppHere'
                            )}
                          </Checkbox>
                        </Col>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="email">
                            {t('frontend:myAccount.notifications.channelEmail')}
                          </Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    <Trans
                      i18nKey="frontend:myAccount.notifications.employeeNewDocumentsDescription"
                      components={{
                        strong: <span style={{ fontWeight: '600' }} />,
                      }}
                    />
                  </Typography.Text>
                </Space>
              </Col>
              {leaveRequestsAsEmployee && (
                <EmailOnlySetting
                  name={['employee', 'leave_request_decided']}
                  value={notificationSettings?.employee?.leave_request_decided}
                  label={t(
                    'frontend:myAccount.notifications.leaveRequestsLabel'
                  )}
                  description={t(
                    'frontend:myAccount.notifications.leaveRequestDecidedDescription'
                  )}
                />
              )}
              <Col span={24} style={{ textAlign: 'right' }}>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {t('frontend:common.save')}
                </Button>
              </Col>
            </Row>
          </Card>
        </>
      )}
      {isEmployer && (
        <>
          <CardHeader style={{ marginTop: token.marginLG }} level={5}>
            {t('frontend:myAccount.notifications.employerSection')}
          </CardHeader>
          <Card bordered={false}>
            <Row gutter={gutter}>
              <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                {t('frontend:myAccount.notifications.newDocumentsLabel')}
              </Col>
              <Col xs={24} md={18}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item
                    style={{ margin: 0 }}
                    name={['employer', 'new_documents']}
                    initialValue={
                      notificationSettings?.employer?.new_documents ?? [
                        'app',
                        'email',
                      ]
                    }
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Row>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="app" disabled checked>
                            {t(
                              'frontend:myAccount.notifications.channelAppHere'
                            )}
                          </Checkbox>
                        </Col>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="email">
                            {t('frontend:myAccount.notifications.channelEmail')}
                          </Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    <Trans
                      i18nKey="frontend:myAccount.notifications.employerNewDocumentsDescription"
                      components={{
                        strong: <span style={{ fontWeight: '600' }} />,
                      }}
                    />
                  </Typography.Text>
                </Space>
              </Col>
              {!isManagedByTaxAdvisor && (
                <>
                  <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                    {t('frontend:myAccount.notifications.newInvoiceLabel')}
                  </Col>
                  <Col xs={24} md={18}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Form.Item
                        style={{ margin: 0 }}
                        name={['employer', 'new_invoice']}
                        initialValue={
                          notificationSettings?.employer?.new_invoice ?? [
                            'app',
                            'email',
                            'email_attachment',
                          ]
                        }
                      >
                        <Checkbox.Group style={{ width: '100%' }}>
                          <Row>
                            <Col xs={24} md={12} xl={8}>
                              <Checkbox value="app" disabled checked>
                                {t(
                                  'frontend:myAccount.notifications.channelAppHere'
                                )}
                              </Checkbox>
                            </Col>
                            <Col xs={24} md={12} xl={8}>
                              <Checkbox value="email">
                                {t(
                                  'frontend:myAccount.notifications.channelEmail'
                                )}
                              </Checkbox>
                            </Col>
                            {employerInvoiceEmail?.includes('email') && (
                              <>
                                <Col xs={0} xl={8} />
                                <Col xs={0} md={12} xl={8} />
                                <Col xs={24} md={12} xl={8}>
                                  <Checkbox value="email_attachment">
                                    {t(
                                      'frontend:myAccount.notifications.channelEmailWithPdf'
                                    )}
                                  </Checkbox>
                                </Col>
                              </>
                            )}
                          </Row>
                        </Checkbox.Group>
                      </Form.Item>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 'small' }}
                      >
                        <Trans
                          i18nKey="frontend:myAccount.notifications.employerNewInvoiceDescription"
                          components={{
                            strong: <span style={{ fontWeight: '600' }} />,
                          }}
                        />
                      </Typography.Text>
                    </Space>
                  </Col>
                </>
              )}
              <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                {t('frontend:myAccount.notifications.payrollReminderLabel')}
              </Col>
              <Col xs={24} md={18}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item
                    style={{ margin: 0 }}
                    name={['employer', 'upcoming_payroll_date']}
                    initialValue={
                      notificationSettings?.employer?.upcoming_payroll_date ?? [
                        'app',
                        'email',
                      ]
                    }
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Row>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="app" disabled checked>
                            {t(
                              'frontend:myAccount.notifications.channelAppHere'
                            )}
                          </Checkbox>
                        </Col>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="email">
                            {t('frontend:myAccount.notifications.channelEmail')}
                          </Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    {t(
                      'frontend:myAccount.notifications.payrollReminderDescription'
                    )}
                  </Typography.Text>
                </Space>
              </Col>
              <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                {t('frontend:myAccount.notifications.newTrialSettlementLabel')}
              </Col>
              <Col xs={24} md={18}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item
                    style={{ margin: 0 }}
                    name={['employer', 'new_trial_settlement']}
                    initialValue={
                      notificationSettings?.employer?.upcoming_payroll_date ?? [
                        'app',
                        'email',
                      ]
                    }
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Row>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="app" disabled checked>
                            {t(
                              'frontend:myAccount.notifications.channelAppHere'
                            )}
                          </Checkbox>
                        </Col>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="email">
                            {t('frontend:myAccount.notifications.channelEmail')}
                          </Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    {t(
                      'frontend:myAccount.notifications.newTrialSettlementDescription'
                    )}
                  </Typography.Text>
                </Space>
              </Col>
              <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                {t('frontend:myAccount.notifications.personalDataChangesLabel')}
              </Col>
              <Col xs={24} md={18}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item
                    style={{ margin: 0 }}
                    name={['employer', 'employee_personal_data_changed']}
                    initialValue={
                      notificationSettings?.employer
                        ?.employee_personal_data_changed ?? ['app', 'email']
                    }
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Row>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="app" disabled checked>
                            {t(
                              'frontend:myAccount.notifications.channelAppHere'
                            )}
                          </Checkbox>
                        </Col>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="email">
                            {t('frontend:myAccount.notifications.channelEmail')}
                          </Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    {t(
                      'frontend:myAccount.notifications.personalDataChangesDescription'
                    )}
                  </Typography.Text>
                </Space>
              </Col>
              <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                {t(
                  'frontend:myAccount.notifications.chatwootCustomerMessageLabel'
                )}
              </Col>
              <Col xs={24} md={18}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item
                    style={{ margin: 0 }}
                    name={['employer', 'chatwoot_customer_message']}
                    initialValue={
                      notificationSettings?.employer
                        ?.chatwoot_customer_message ?? ['app', 'email']
                    }
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Row>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="app" disabled checked>
                            {t(
                              'frontend:myAccount.notifications.channelAppHere'
                            )}
                          </Checkbox>
                        </Col>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="email">
                            {t('frontend:myAccount.notifications.channelEmail')}
                          </Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    {t(
                      'frontend:myAccount.notifications.chatwootCustomerMessageDescription'
                    )}
                  </Typography.Text>
                </Space>
              </Col>
              {leaveRequestsAsEmployer && (
                <EmailOnlySetting
                  name={['employer', 'leave_request_submitted']}
                  value={
                    notificationSettings?.employer?.leave_request_submitted
                  }
                  label={t(
                    'frontend:myAccount.notifications.leaveRequestsLabel'
                  )}
                  description={t(
                    'frontend:myAccount.notifications.leaveRequestSubmittedDescription'
                  )}
                />
              )}
              <Col span={24} style={{ textAlign: 'right' }}>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {t('frontend:common.save')}
                </Button>
              </Col>
            </Row>
          </Card>
        </>
      )}
      {isTaxAdvisor && (
        <>
          <CardHeader style={{ marginTop: token.marginLG }} level={5}>
            {t('frontend:myAccount.notifications.taxAdvisorSection')}
          </CardHeader>
          <Card bordered={false}>
            <Row gutter={gutter}>
              <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                {t('frontend:myAccount.notifications.newInvoiceLabel')}
              </Col>
              <Col xs={24} md={18}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item
                    style={{ margin: 0 }}
                    name={['tax-advisor', 'new_invoice']}
                    initialValue={
                      notificationSettings?.['tax-advisor']?.new_invoice ?? [
                        'app',
                        'email',
                        'email_attachment',
                      ]
                    }
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Row>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="app" disabled checked>
                            {t(
                              'frontend:myAccount.notifications.channelAppHere'
                            )}
                          </Checkbox>
                        </Col>
                        <Col xs={24} md={12} xl={8}>
                          <Checkbox value="email">
                            {t('frontend:myAccount.notifications.channelEmail')}
                          </Checkbox>
                        </Col>
                        {taxAdvisorInvoiceEmail?.includes('email') && (
                          <>
                            <Col xs={0} xl={8} />
                            <Col xs={0} md={12} xl={8} />
                            <Col xs={24} md={12} xl={8}>
                              <Checkbox value="email_attachment">
                                {t(
                                  'frontend:myAccount.notifications.channelEmailWithPdf'
                                )}
                              </Checkbox>
                            </Col>
                          </>
                        )}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    <Trans
                      i18nKey="frontend:myAccount.notifications.taxAdvisorNewInvoiceDescription"
                      components={{
                        strong: <span style={{ fontWeight: '600' }} />,
                      }}
                    />
                  </Typography.Text>
                </Space>
              </Col>
              <Col span={24} style={{ textAlign: 'right' }}>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {t('frontend:common.save')}
                </Button>
              </Col>
            </Row>
          </Card>
        </>
      )}
      {canUseSupportInbox && (
        <>
          <CardHeader style={{ marginTop: token.marginLG }} level={5}>
            {t('frontend:myAccount.notifications.chatwootHeading')}
          </CardHeader>
          <Card bordered={false}>
            <Row gutter={gutter}>
              <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                {t('frontend:myAccount.notifications.soundLabel')}
              </Col>
              <Col xs={24} md={18}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {/* Without this the inbox chimes for every incoming message
                      in the whole account, and muting the tab is the only way
                      to stop it — which also silences Pulse. */}
                  <Form.Item
                    style={{ margin: 0 }}
                    name={['chatwoot', 'sound']}
                    initialValue={asChatwootSoundMode(
                      notificationSettings?.chatwoot?.sound
                    )}
                  >
                    <Radio.Group>
                      <Space direction="vertical">
                        <Radio value="all">
                          {t(
                            'frontend:myAccount.notifications.chatwootSoundAll'
                          )}
                        </Radio>
                        <Radio value="mine">
                          {t(
                            'frontend:myAccount.notifications.chatwootSoundMine'
                          )}
                        </Radio>
                        <Radio value="off">
                          {t(
                            'frontend:myAccount.notifications.chatwootSoundOff'
                          )}
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    {t(
                      'frontend:myAccount.notifications.chatwootSoundDescription'
                    )}
                  </Typography.Text>
                </Space>
              </Col>
              <Col span={24} style={{ textAlign: 'right' }}>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {t('frontend:common.save')}
                </Button>
              </Col>
            </Row>
          </Card>
        </>
      )}
      {isAutomationAdmin && (
        <>
          <CardHeader style={{ marginTop: token.marginLG }} level={5}>
            Pulse
          </CardHeader>
          <Card bordered={false}>
            <Row gutter={gutter}>
              <Col xs={24} md={6} style={{ fontWeight: '500' }}>
                {t('frontend:myAccount.notifications.soundLabel')}
              </Col>
              <Col xs={24} md={18}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item
                    style={{ margin: 0 }}
                    name={['agent', 'sound_enabled']}
                    valuePropName="checked"
                    initialValue={
                      notificationSettings?.agent?.sound_enabled ?? true
                    }
                  >
                    <Switch />
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    {t('frontend:myAccount.notifications.soundDescription')}
                  </Typography.Text>
                </Space>
              </Col>
              <Col span={24} style={{ textAlign: 'right' }}>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {t('frontend:common.save')}
                </Button>
              </Col>
            </Row>
          </Card>
        </>
      )}
    </Form>
  )
}
